import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

// UI Components
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

// Icons
import { Utensils, Bus, Plus, Calendar, MapPin, Clock, Users, AlertTriangle, CheckCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CantineTransportManagement = ({ user }) => {
  const [menus, setMenus] = useState([]);
  const [cantineReservations, setCantineReservations] = useState([]);
  const [transportRoutes, setTransportRoutes] = useState([]);
  const [transportSubscriptions, setTransportSubscriptions] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('cantine');
  
  // Dialog states
  const [menuDialogOpen, setMenuDialogOpen] = useState(false);
  const [reservationDialogOpen, setReservationDialogOpen] = useState(false);
  const [routeDialogOpen, setRouteDialogOpen] = useState(false);
  const [subscriptionDialogOpen, setSubscriptionDialogOpen] = useState(false);
  
  const [menuForm, setMenuForm] = useState({
    date: new Date().toISOString().split('T')[0],
    meal_type: '',
    name: '',
    description: '',
    ingredients: [],
    allergens: [],
    price: '',
    available_quantity: '',
    dietary_options: []
  });

  const [reservationForm, setReservationForm] = useState({
    student_id: '',
    menu_day_id: '',
    reservation_date: new Date().toISOString().split('T')[0],
    meal_type: '',
    special_diet: 'NONE',
    allergies_notes: ''
  });

  const [routeForm, setRouteForm] = useState({
    route_name: '',
    description: '',
    stops: [],
    monthly_price: '',
    capacity: '',
    driver_name: '',
    vehicle_info: ''
  });

  const [subscriptionForm, setSubscriptionForm] = useState({
    student_id: '',
    route_id: '',
    stop_pickup: '',
    stop_dropoff: '',
    start_date: new Date().toISOString().split('T')[0],
    end_date: ''
  });

  const mealTypes = [
    { value: 'BREAKFAST', label: 'Petit-déjeuner', icon: '🌅' },
    { value: 'LUNCH', label: 'Déjeuner', icon: '🍽️' },
    { value: 'SNACK', label: 'Goûter', icon: '🍎' },
    { value: 'DINNER', label: 'Dîner', icon: '🌙' }
  ];

  const dietaryOptions = [
    { value: 'NONE', label: 'Aucune restriction' },
    { value: 'VEGETARIAN', label: 'Végétarien' },
    { value: 'VEGAN', label: 'Végan' },
    { value: 'HALAL', label: 'Halal' },
    { value: 'KOSHER', label: 'Casher' },
    { value: 'GLUTEN_FREE', label: 'Sans gluten' },
    { value: 'LACTOSE_FREE', label: 'Sans lactose' },
    { value: 'NUT_ALLERGY', label: 'Allergie aux noix' }
  ];

  const commonAllergens = [
    'Gluten', 'Œufs', 'Lait', 'Arachides', 'Fruits à coque', 
    'Soja', 'Poisson', 'Crustacés', 'Mollusques', 'Céleri', 
    'Moutarde', 'Sésame', 'Sulfites', 'Lupin'
  ];

  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    try {
      const promises = [
        axios.get(`${API}/menus`),
        axios.get(`${API}/cantine-reservations`),
        axios.get(`${API}/transport-routes`),
        axios.get(`${API}/transport-subscriptions`)
      ];

      if (user.role !== 'PARENT' && user.role !== 'STUDENT') {
        promises.push(axios.get(`${API}/students`));
      }

      const results = await Promise.all(promises);

      setMenus(results[0].data);
      setCantineReservations(results[1].data);
      setTransportRoutes(results[2].data);
      setTransportSubscriptions(results[3].data);
      
      if (results[4]) {
        setStudents(results[4].data);
      }
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMenuSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/menus`, {
        ...menuForm,
        date: new Date(menuForm.date).toISOString(),
        price: parseFloat(menuForm.price),
        available_quantity: menuForm.available_quantity ? parseInt(menuForm.available_quantity) : null,
        ingredients: menuForm.ingredients.filter(ing => ing.trim()),
        allergens: menuForm.allergens,
        dietary_options: menuForm.dietary_options
      });

      setMenus(prev => [response.data, ...prev]);
      toast.success('Menu créé avec succès');
      resetMenuForm();
      setMenuDialogOpen(false);
    } catch (error) {
      toast.error('Erreur lors de la création du menu');
      console.error('Error creating menu:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleReservationSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/cantine-reservations`, {
        ...reservationForm,
        reservation_date: new Date(reservationForm.reservation_date).toISOString()
      });

      setCantineReservations(prev => [response.data, ...prev]);
      toast.success('Réservation effectuée avec succès');
      resetReservationForm();
      setReservationDialogOpen(false);
    } catch (error) {
      toast.error('Erreur lors de la réservation');
      console.error('Error creating reservation:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRouteSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/transport-routes`, {
        ...routeForm,
        monthly_price: parseFloat(routeForm.monthly_price),
        capacity: parseInt(routeForm.capacity),
        stops: routeForm.stops.filter(stop => stop.name && stop.address)
      });

      setTransportRoutes(prev => [response.data, ...prev]);
      toast.success('Ligne de transport créée avec succès');
      resetRouteForm();
      setRouteDialogOpen(false);
    } catch (error) {
      toast.error('Erreur lors de la création de la ligne');
      console.error('Error creating route:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubscriptionSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/transport-subscriptions`, {
        ...subscriptionForm,
        start_date: new Date(subscriptionForm.start_date).toISOString(),
        end_date: new Date(subscriptionForm.end_date).toISOString()
      });

      setTransportSubscriptions(prev => [response.data, ...prev]);
      toast.success('Abonnement transport souscrit avec succès');
      resetSubscriptionForm();
      setSubscriptionDialogOpen(false);
    } catch (error) {
      toast.error('Erreur lors de la souscription');
      console.error('Error creating subscription:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetMenuForm = () => {
    setMenuForm({
      date: new Date().toISOString().split('T')[0],
      meal_type: '',
      name: '',
      description: '',
      ingredients: [],
      allergens: [],
      price: '',
      available_quantity: '',
      dietary_options: []
    });
  };

  const resetReservationForm = () => {
    setReservationForm({
      student_id: '',
      menu_day_id: '',
      reservation_date: new Date().toISOString().split('T')[0],
      meal_type: '',
      special_diet: 'NONE',
      allergies_notes: ''
    });
  };

  const resetRouteForm = () => {
    setRouteForm({
      route_name: '',
      description: '',
      stops: [],
      monthly_price: '',
      capacity: '',
      driver_name: '',
      vehicle_info: ''
    });
  };

  const resetSubscriptionForm = () => {
    setSubscriptionForm({
      student_id: '',
      route_id: '',
      stop_pickup: '',
      stop_dropoff: '',
      start_date: new Date().toISOString().split('T')[0],
      end_date: ''
    });
  };

  const getStudentName = (studentId) => {
    const student = students.find(s => s.id === studentId);
    return student ? `${student.first_name} ${student.last_name}` : 'Élève inconnu';
  };

  const getMealTypeInfo = (type) => {
    return mealTypes.find(t => t.value === type) || mealTypes[0];
  };

  const getDietaryLabel = (diet) => {
    const option = dietaryOptions.find(d => d.value === diet);
    return option ? option.label : diet;
  };

  const canManageServices = ['ADMIN'].includes(user.role);

  if (loading && menus.length === 0) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/3" />
          <div className="h-64 bg-gray-200 rounded" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 fade-in" data-testid="cantine-transport-management">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold flex items-center">
            <Utensils className="w-6 h-6 mr-2 text-blue-600" />
            Cantine & Transport
          </h1>
          <p className="text-gray-600 mt-1">
            Gestion des repas, réservations et transport scolaire
          </p>
        </div>

        {canManageServices && (
          <div className="flex space-x-3">
            <Dialog open={menuDialogOpen} onOpenChange={setMenuDialogOpen}>
              <DialogTrigger asChild>
                <Button 
                  data-testid="create-menu-btn"
                  onClick={() => { resetMenuForm(); setMenuDialogOpen(true); }}
                  className="btn-success"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Nouveau Menu
                </Button>
              </DialogTrigger>

              <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Créer un menu</DialogTitle>
                  <DialogDescription>
                    Ajoutez un nouveau menu à la cantine
                  </DialogDescription>
                </DialogHeader>

                <form onSubmit={handleMenuSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="menu_date">Date</Label>
                      <Input
                        id="menu_date"
                        type="date"
                        value={menuForm.date}
                        onChange={(e) => setMenuForm({ ...menuForm, date: e.target.value })}
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="meal_type">Type de repas</Label>
                      <Select 
                        value={menuForm.meal_type} 
                        onValueChange={(value) => setMenuForm({ ...menuForm, meal_type: value })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Type de repas" />
                        </SelectTrigger>
                        <SelectContent>
                          {mealTypes.map((type) => (
                            <SelectItem key={type.value} value={type.value}>
                              {type.icon} {type.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="menu_name">Nom du menu</Label>
                    <Input
                      id="menu_name"
                      value={menuForm.name}
                      onChange={(e) => setMenuForm({ ...menuForm, name: e.target.value })}
                      placeholder="ex: Menu du jour, Menu végétarien..."
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="menu_description">Description</Label>
                    <Textarea
                      id="menu_description"
                      value={menuForm.description}
                      onChange={(e) => setMenuForm({ ...menuForm, description: e.target.value })}
                      placeholder="Décrivez le menu du jour..."
                      rows={3}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="ingredients">Ingrédients (séparés par des virgules)</Label>
                    <Textarea
                      id="ingredients"
                      value={menuForm.ingredients.join(', ')}
                      onChange={(e) => setMenuForm({ ...menuForm, ingredients: e.target.value.split(',').map(ing => ing.trim()) })}
                      placeholder="Tomates, salade, fromage, pain..."
                      rows={2}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Allergènes présents</Label>
                    <div className="grid grid-cols-3 gap-2">
                      {commonAllergens.map((allergen) => (
                        <div key={allergen} className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            id={`allergen-${allergen}`}
                            checked={menuForm.allergens.includes(allergen)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setMenuForm({ ...menuForm, allergens: [...menuForm.allergens, allergen] });
                              } else {
                                setMenuForm({ ...menuForm, allergens: menuForm.allergens.filter(a => a !== allergen) });
                              }
                            }}
                            className="rounded border-gray-300"
                          />
                          <Label htmlFor={`allergen-${allergen}`} className="text-sm">
                            {allergen}
                          </Label>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="price">Prix (GNF)</Label>
                      <Input
                        id="price"
                        type="number"
                        step="0.01"
                        min="0"
                        value={menuForm.price}
                        onChange={(e) => setMenuForm({ ...menuForm, price: e.target.value })}
                        placeholder="4.50"
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="quantity">Quantité disponible (optionnel)</Label>
                      <Input
                        id="quantity"
                        type="number"
                        min="1"
                        value={menuForm.available_quantity}
                        onChange={(e) => setMenuForm({ ...menuForm, available_quantity: e.target.value })}
                        placeholder="100"
                      />
                    </div>
                  </div>

                  <div className="flex justify-end space-x-3">
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={() => setMenuDialogOpen(false)}
                    >
                      Annuler
                    </Button>
                    <Button 
                      type="submit" 
                      disabled={loading}
                      className="btn-success"
                    >
                      {loading ? 'Création...' : 'Créer le menu'}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>

            <Dialog open={routeDialogOpen} onOpenChange={setRouteDialogOpen}>
              <DialogTrigger asChild>
                <Button 
                  onClick={() => { resetRouteForm(); setRouteDialogOpen(true); }}
                  className="btn-secondary"
                >
                  <Bus className="w-4 h-4 mr-2" />
                  Nouvelle Ligne
                </Button>
              </DialogTrigger>

              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>Créer une ligne de transport</DialogTitle>
                  <DialogDescription>
                    Ajoutez une nouvelle ligne de transport scolaire
                  </DialogDescription>
                </DialogHeader>

                <form onSubmit={handleRouteSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="route_name">Nom de la ligne</Label>
                    <Input
                      id="route_name"
                      value={routeForm.route_name}
                      onChange={(e) => setRouteForm({ ...routeForm, route_name: e.target.value })}
                      placeholder="ex: Ligne A - Centre-ville"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="route_description">Description</Label>
                    <Textarea
                      id="route_description"
                      value={routeForm.description}
                      onChange={(e) => setRouteForm({ ...routeForm, description: e.target.value })}
                      placeholder="Description de la ligne..."
                      rows={2}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="monthly_price">Prix mensuel (GNF)</Label>
                      <Input
                        id="monthly_price"
                        type="number"
                        step="0.01"
                        min="0"
                        value={routeForm.monthly_price}
                        onChange={(e) => setRouteForm({ ...routeForm, monthly_price: e.target.value })}
                        placeholder="45.00"
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="capacity">Capacité</Label>
                      <Input
                        id="capacity"
                        type="number"
                        min="1"
                        value={routeForm.capacity}
                        onChange={(e) => setRouteForm({ ...routeForm, capacity: e.target.value })}
                        placeholder="50"
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="driver_name">Chauffeur</Label>
                      <Input
                        id="driver_name"
                        value={routeForm.driver_name}
                        onChange={(e) => setRouteForm({ ...routeForm, driver_name: e.target.value })}
                        placeholder="Nom du chauffeur"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="vehicle_info">Véhicule</Label>
                      <Input
                        id="vehicle_info"
                        value={routeForm.vehicle_info}
                        onChange={(e) => setRouteForm({ ...routeForm, vehicle_info: e.target.value })}
                        placeholder="Bus Mercedes - AB-123-CD"
                      />
                    </div>
                  </div>

                  <div className="flex justify-end space-x-3">
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={() => setRouteDialogOpen(false)}
                    >
                      Annuler
                    </Button>
                    <Button 
                      type="submit" 
                      disabled={loading}
                      className="btn-secondary"
                    >
                      {loading ? 'Création...' : 'Créer la ligne'}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        )}
      </div>

      {/* Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="cantine" className="flex items-center">
            <Utensils className="w-4 h-4 mr-2" />
            Cantine
          </TabsTrigger>
          <TabsTrigger value="transport" className="flex items-center">
            <Bus className="w-4 h-4 mr-2" />
            Transport
          </TabsTrigger>
        </TabsList>

        {/* Cantine Tab */}
        <TabsContent value="cantine" className="space-y-6">
          {/* Menu List */}
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold">Menus disponibles</h2>
              <Dialog open={reservationDialogOpen} onOpenChange={setReservationDialogOpen}>
                <DialogTrigger asChild>
                  <Button 
                    onClick={() => { resetReservationForm(); setReservationDialogOpen(true); }}
                    className="btn-primary"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Réserver un repas
                  </Button>
                </DialogTrigger>

                <DialogContent className="max-w-lg">
                  <DialogHeader>
                    <DialogTitle>Réserver un repas</DialogTitle>
                    <DialogDescription>
                      Réservez un repas à la cantine
                    </DialogDescription>
                  </DialogHeader>

                  <form onSubmit={handleReservationSubmit} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="reservation_student">Élève</Label>
                      <Select 
                        value={reservationForm.student_id} 
                        onValueChange={(value) => setReservationForm({ ...reservationForm, student_id: value })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Sélectionnez un élève" />
                        </SelectTrigger>
                        <SelectContent>
                          {students.map((student) => (
                            <SelectItem key={student.id} value={student.id}>
                              {student.first_name} {student.last_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="menu_selection">Menu</Label>
                      <Select 
                        value={reservationForm.menu_day_id} 
                        onValueChange={(value) => setReservationForm({ ...reservationForm, menu_day_id: value })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Choisissez un menu" />
                        </SelectTrigger>
                        <SelectContent>
                          {menus.map((menu) => (
                            <SelectItem key={menu.id} value={menu.id}>
                              {menu.name} - {new Date(menu.date).toLocaleDateString('fr-FR')} - {menu.price.toLocaleString()} GNF
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="special_diet">Régime spécial</Label>
                      <Select 
                        value={reservationForm.special_diet} 
                        onValueChange={(value) => setReservationForm({ ...reservationForm, special_diet: value })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {dietaryOptions.map((diet) => (
                            <SelectItem key={diet.value} value={diet.value}>
                              {diet.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="allergies_notes">Notes allergies (optionnel)</Label>
                      <Textarea
                        id="allergies_notes"
                        value={reservationForm.allergies_notes}
                        onChange={(e) => setReservationForm({ ...reservationForm, allergies_notes: e.target.value })}
                        placeholder="Précisions sur les allergies..."
                        rows={2}
                      />
                    </div>

                    <div className="flex justify-end space-x-3">
                      <Button 
                        type="button" 
                        variant="outline" 
                        onClick={() => setReservationDialogOpen(false)}
                      >
                        Annuler
                      </Button>
                      <Button 
                        type="submit" 
                        disabled={loading}
                        className="btn-primary"
                      >
                        {loading ? 'Réservation...' : 'Réserver'}
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {menus.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {menus.map((menu) => {
                  const mealTypeInfo = getMealTypeInfo(menu.meal_type);

                  return (
                    <Card key={menu.id} className="card">
                      <CardHeader className="pb-3">
                        <div className="flex justify-between items-start">
                          <div>
                            <CardTitle className="text-lg">{menu.name}</CardTitle>
                            <div className="flex items-center space-x-2 mt-2">
                              <Badge className="badge-primary">
                                {mealTypeInfo.icon} {mealTypeInfo.label}
                              </Badge>
                              <Badge className="badge-success">
                                {menu.price.toLocaleString()} GNF
                              </Badge>
                            </div>
                          </div>
                        </div>
                      </CardHeader>

                      <CardContent>
                        <div className="space-y-3">
                          <p className="text-sm text-gray-700">
                            {menu.description}
                          </p>

                          <div className="text-sm">
                            <span className="text-gray-600">Date:</span>
                            <span className="font-medium ml-1">
                              {new Date(menu.date).toLocaleDateString('fr-FR')}
                            </span>
                          </div>

                          {menu.ingredients && menu.ingredients.length > 0 && (
                            <div className="text-sm">
                              <span className="text-gray-600">Ingrédients:</span>
                              <p className="text-gray-700 mt-1">
                                {menu.ingredients.join(', ')}
                              </p>
                            </div>
                          )}

                          {menu.allergens && menu.allergens.length > 0 && (
                            <div className="text-sm">
                              <div className="flex items-center text-red-600 mb-1">
                                <AlertTriangle className="w-4 h-4 mr-1" />
                                <span className="font-medium">Allergènes:</span>
                              </div>
                              <div className="flex flex-wrap gap-1">
                                {menu.allergens.map((allergen, index) => (
                                  <Badge key={index} className="badge-danger text-xs">
                                    {allergen}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          )}

                          {menu.available_quantity && (
                            <div className="text-sm text-gray-600">
                              Places restantes: {menu.available_quantity}
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-12">
                <Utensils className="w-16 h-16 mx-auto text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun menu disponible</h3>
                <p className="text-gray-600">
                  {canManageServices ? 'Créez le premier menu de la cantine.' : 'Les menus seront bientôt disponibles.'}
                </p>
              </div>
            )}
          </div>

          {/* Reservations List */}
          {cantineReservations.length > 0 && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Mes réservations</h3>
              <div className="space-y-3">
                {cantineReservations.map((reservation) => (
                  <Card key={reservation.id} className="card">
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">
                            {user.role !== 'STUDENT' && `${getStudentName(reservation.student_id)} - `}
                            Repas du {new Date(reservation.reservation_date).toLocaleDateString('fr-FR')}
                          </p>
                          <p className="text-sm text-gray-600 mt-1">
                            Régime: {getDietaryLabel(reservation.special_diet)}
                          </p>
                          {reservation.allergies_notes && (
                            <p className="text-sm text-gray-700 mt-1 italic">
                              Allergies: {reservation.allergies_notes}
                            </p>
                          )}
                        </div>
                        <Badge className={`badge-${reservation.status === 'CONFIRMED' ? 'success' : reservation.status === 'CANCELLED' ? 'danger' : 'warning'}`}>
                          {reservation.status}
                        </Badge>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </TabsContent>

        {/* Transport Tab */}
        <TabsContent value="transport" className="space-y-6">
          {/* Routes List */}
          <div className="space-y-4">
            <h2 className="text-xl font-semibold">Lignes de transport</h2>

            {transportRoutes.length > 0 ? (
              <div className="space-y-4">
                {transportRoutes.map((route) => (
                  <Card key={route.id} className="card">
                    <CardContent className="pt-6">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <h3 className="font-semibold text-lg">{route.route_name}</h3>
                            <Badge className="badge-primary">
                              {route.monthly_price.toLocaleString()} GNF/mois
                            </Badge>
                            <Badge className="badge-secondary">
                              <Users className="w-3 h-3 mr-1" />
                              {route.capacity} places
                            </Badge>
                          </div>

                          {route.description && (
                            <p className="text-sm text-gray-700 mb-3">{route.description}</p>
                          )}

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                            {route.driver_name && (
                              <div>
                                <span className="text-gray-600">Chauffeur:</span>
                                <span className="font-medium ml-1">{route.driver_name}</span>
                              </div>
                            )}

                            {route.vehicle_info && (
                              <div>
                                <span className="text-gray-600">Véhicule:</span>
                                <span className="font-medium ml-1">{route.vehicle_info}</span>
                              </div>
                            )}
                          </div>

                          <div className="mt-4">
                            <Button
                              onClick={() => {
                                setSubscriptionForm({ ...subscriptionForm, route_id: route.id });
                                setSubscriptionDialogOpen(true);
                              }}
                              className="btn-secondary"
                              size="sm"
                            >
                              <Bus className="w-4 h-4 mr-2" />
                              S'abonner
                            </Button>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <Bus className="w-16 h-16 mx-auto text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Aucune ligne de transport</h3>
                <p className="text-gray-600">
                  {canManageServices ? 'Créez la première ligne de transport.' : 'Les lignes seront bientôt disponibles.'}
                </p>
              </div>
            )}
          </div>

          {/* Transport Subscription Dialog */}
          <Dialog open={subscriptionDialogOpen} onOpenChange={setSubscriptionDialogOpen}>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Souscrire à un transport</DialogTitle>
                <DialogDescription>
                  Abonnez-vous à une ligne de transport scolaire
                </DialogDescription>
              </DialogHeader>

              <form onSubmit={handleSubscriptionSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="sub_student">Élève</Label>
                  <Select 
                    value={subscriptionForm.student_id} 
                    onValueChange={(value) => setSubscriptionForm({ ...subscriptionForm, student_id: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionnez un élève" />
                    </SelectTrigger>
                    <SelectContent>
                      {students.map((student) => (
                        <SelectItem key={student.id} value={student.id}>
                          {student.first_name} {student.last_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="stop_pickup">Arrêt montée</Label>
                    <Input
                      id="stop_pickup"
                      value={subscriptionForm.stop_pickup}
                      onChange={(e) => setSubscriptionForm({ ...subscriptionForm, stop_pickup: e.target.value })}
                      placeholder="Nom de l'arrêt"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="stop_dropoff">Arrêt descente</Label>
                    <Input
                      id="stop_dropoff"
                      value={subscriptionForm.stop_dropoff}
                      onChange={(e) => setSubscriptionForm({ ...subscriptionForm, stop_dropoff: e.target.value })}
                      placeholder="Nom de l'arrêt"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="start_date">Date de début</Label>
                    <Input
                      id="start_date"
                      type="date"
                      value={subscriptionForm.start_date}
                      onChange={(e) => setSubscriptionForm({ ...subscriptionForm, start_date: e.target.value })}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="end_date">Date de fin</Label>
                    <Input
                      id="end_date"
                      type="date"
                      value={subscriptionForm.end_date}
                      onChange={(e) => setSubscriptionForm({ ...subscriptionForm, end_date: e.target.value })}
                      required
                    />
                  </div>
                </div>

                <div className="flex justify-end space-x-3">
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => setSubscriptionDialogOpen(false)}
                  >
                    Annuler
                  </Button>
                  <Button 
                    type="submit" 
                    disabled={loading}
                    className="btn-secondary"
                  >
                    {loading ? 'Souscription...' : 'Souscrire'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default CantineTransportManagement;